import { createFileRoute, Link } from "@tanstack/react-router";

import { PageHero, Prose, ProseSection } from "@/components/page-hero";

export const Route = createFileRoute("/tipping")({
  head: () => ({
    meta: [
      { title: "Tipping & Platform Fee Policy — Quibblies" },
      {
        name: "description",
        content:
          "How reader tips work on Quibblies: the 5% platform fee, payment processing costs, the £10 minimum payout, payout timings, refunds and disputes.",
      },
      { property: "og:title", content: "Tipping & Platform Fee Policy — Quibblies" },
      {
        property: "og:description",
        content:
          "Quibblies keeps a 5% platform fee on reader tips. Everything else, minus payment processing, goes to the writer.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: TippingPolicyPage,
});

function TippingPolicyPage() {
  return (
    <div>
      <PageHero
        eyebrow="Money"
        title="Tipping and platform fee policy"
        description="Readers can tip writers directly. This page sets out exactly what is deducted, what a writer receives, and when they receive it."
      />
      <Prose>
        <ProseSection title="The platform fee">
          <p>
            Quibblies keeps a <strong>5% platform fee</strong> on every completed tip. The fee is
            calculated on the gross tip amount and is shown to the reader before they pay and to
            the writer on every receipt. It covers moderation, hosting, fraud checks and support.
          </p>
          <p>
            Payment processing costs charged by our payment provider are also deducted from the
            tip. The remaining <strong>net amount</strong> is credited to the writer's balance.
          </p>
          <p>
            Example: a £10.00 tip is recorded as £10.00 gross, a £0.50 Quibblies fee, and the
            balance credited to the writer as net, minus payment processing.
          </p>
        </ProseSection>
        <ProseSection title="Tips are voluntary">
          <p>
            A tip is a voluntary gift of support. It does not buy access to writing, private
            content, a commission, or any promise from a writer. Writing on Quibblies stays free to
            read. Writers must never gate content behind a tip.
          </p>
        </ProseSection>
        <ProseSection title="Age and identity verification (required before payout)">
          <p>
            Quibblies only releases money to writers whose age and identity have been verified.
            Before you can set up a payout account or request a payout, you must upload photo ID
            in Settings &rarr; Profile &amp; cover images and have it approved by an
            administrator.
          </p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              ID documents are stored privately and are visible only to administrators reviewing
              them. They are never shown on your profile or shared with other members.
            </li>
            <li>
              Tips can still be received while verification is pending — the balance simply cannot
              be paid out until your verification is approved.
            </li>
            <li>
              If your verification is rejected or expires, payouts pause until you submit new
              proof.
            </li>
          </ul>
        </ProseSection>
        <ProseSection title="Payouts">
          <ul className="list-disc space-y-1 pl-5">
            <li>Approved age/ID verification is required before any payout is released.</li>
            <li>Balances are paid out manually by the Quibblies team.</li>
            <li>
              The minimum payout is <strong>£10.00</strong> net. Balances below that roll over to
              the next payout run.
            </li>
            <li>
              Once an administrator approves a payout, funds typically take{" "}
              <strong>3–5 working days</strong> to reach the writer's bank account.
            </li>
            <li>
              Each payout records the method and reference, and is visible to the writer in Creator
              Studio.
            </li>
          </ul>
        </ProseSection>
        <ProseSection title="Refunds, chargebacks and disputes">
          <p>
            If a tip is refunded or a reader raises a chargeback before the money has been paid
            out, the tip is reversed and the amount is removed from the writer's balance, including
            any platform fee already recorded.
          </p>
          <p>
            <strong>
              Once a tip has been paid out to the writer, it is final and non-refundable.
            </strong>{" "}
            Quibblies cannot reverse a completed payout and is not responsible for recovering money
            already sent to a writer. If a chargeback is raised after a payout has been made, the
            amount is recovered from the writer's future tips where possible; any remaining
            shortfall is a matter between the reader, their bank and the writer. Tipping is
            voluntary — please only tip an amount you are comfortable giving.
          </p>
          <p>
            Tips connected to fraud, stolen payment details, coercion or attempts to buy moderation
            outcomes are reversed and may lead to account action.
          </p>
        </ProseSection>

        <ProseSection title="Tax">
          <p>
            Writers are responsible for declaring tip income to their own tax authority. Quibblies
            does not withhold tax and does not provide tax advice.
          </p>
        </ProseSection>
        <ProseSection title="Changes to the fee">
          <p>
            If the platform fee changes, the new rate applies only to tips made after the change.
            Tips already recorded keep the fee that applied at the time.
          </p>
          <p>
            Questions about a specific tip or payout? See the{" "}
            <Link to="/support" className="text-primary underline-offset-4 hover:underline">
              Support &amp; FAQ
            </Link>{" "}
            page or{" "}
            <Link to="/contact" className="text-primary underline-offset-4 hover:underline">
              contact the team
            </Link>
            .
          </p>
        </ProseSection>
      </Prose>
    </div>
  );
}
